import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Minus, Plus, Trash2, ArrowLeft, ShoppingCart, CheckCircle, CreditCard, Truck, Loader2, ChevronDown } from "lucide-react";

const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p);

export default function CartPage() {
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("cart");
  const [order, setOrder] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [midtransReady, setMidtransReady] = useState(false);

  // Shipping address - cascading
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subdistricts, setSubdistricts] = useState([]);

  const [selProvince, setSelProvince] = useState("");
  const [selCity, setSelCity] = useState("");
  const [selDistrict, setSelDistrict] = useState("");
  const [selSubdistrict, setSelSubdistrict] = useState("");

  const [provinceName, setProvinceName] = useState("");
  const [cityName, setCityName] = useState("");
  const [districtName, setDistrictName] = useState("");
  const [subdistrictName, setSubdistrictName] = useState("");
  const [postalCode, setPostalCode] = useState("");

  // Shipping cost
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [calcShipping, setCalcShipping] = useState(false);

  const [form, setForm] = useState({ name: "", phone: "", email: "", address_detail: "", notes: "" });

  useEffect(() => { fetchCart(); loadMidtrans(); loadProvinces(); }, []);

  const fetchCart = async () => {
    try { const { data } = await api.get("/cart"); setCart(data); } catch {} finally { setLoading(false); }
  };

  const loadMidtrans = async () => {
    try {
      const { data } = await api.get("/payments/client-key");
      if (data.configured && data.client_key) {
        const existing = document.getElementById("midtrans-snap-script");
        if (existing) existing.remove();
        const script = document.createElement("script");
        script.id = "midtrans-snap-script";
        script.src = data.snap_url || (data.is_sandbox ? "https://app.sandbox.midtrans.com/snap/snap.js" : "https://app.midtrans.com/snap/snap.js");
        script.setAttribute("data-client-key", data.client_key);
        script.async = true;
        script.onload = () => setMidtransReady(true);
        document.head.appendChild(script);
      }
    } catch {}
  };

  const loadProvinces = async () => {
    try {
      const { data } = await api.get("/shipping/provinces");
      setProvinces(data.data || []);
    } catch {}
  };

  const onProvinceChange = async (val) => {
    setSelProvince(val);
    const prov = provinces.find(p => String(p.id) === val);
    setProvinceName(prov?.name || "");
    setSelCity(""); setCityName(""); setSelDistrict(""); setDistrictName(""); setSelSubdistrict(""); setSubdistrictName("");
    setCities([]); setDistricts([]); setSubdistricts([]); setPostalCode("");
    setShippingOptions([]); setSelectedShipping(null);
    try {
      const { data } = await api.get(`/shipping/cities/${val}`);
      setCities(data.data || []);
    } catch {}
  };

  const onCityChange = async (val) => {
    setSelCity(val);
    const c = cities.find(x => String(x.id) === val);
    setCityName(c?.name || "");
    setSelDistrict(""); setDistrictName(""); setSelSubdistrict(""); setSubdistrictName("");
    setDistricts([]); setSubdistricts([]); setPostalCode(c?.zip_code && c.zip_code !== "0" ? c.zip_code : "");
    setShippingOptions([]); setSelectedShipping(null);
    try {
      const { data } = await api.get(`/shipping/districts/${val}`);
      setDistricts(data.data || []);
    } catch {}
  };

  const onDistrictChange = async (val) => {
    setSelDistrict(val);
    const d = districts.find(x => String(x.id) === val);
    setDistrictName(d?.name || "");
    setSelSubdistrict(""); setSubdistrictName("");
    setSubdistricts([]);
    if (d?.zip_code && d.zip_code !== "0") setPostalCode(d.zip_code);
    setShippingOptions([]); setSelectedShipping(null);
    try {
      const { data } = await api.get(`/shipping/subdistricts/${val}`);
      setSubdistricts(data.data || []);
    } catch {}
    // Auto calculate shipping
    calculateShipping(val);
  };

  const onSubdistrictChange = (val) => {
    setSelSubdistrict(val);
    const s = subdistricts.find(x => String(x.id) === val);
    setSubdistrictName(s?.name || "");
    if (s?.zip_code && s.zip_code !== "0") setPostalCode(s.zip_code);
  };

  const calculateShipping = async (districtId) => {
    setCalcShipping(true);
    setShippingOptions([]);
    setSelectedShipping(null);
    try {
      const { data: originData } = await api.get("/shipping/origin");
      const origin = originData?.origin || "";
      if (!origin) {
        toast.info("Origin toko belum dikonfigurasi admin.");
        setCalcShipping(false);
        return;
      }
      const totalWeight = items.reduce((s, i) => s + (i.weight || 200) * i.qty, 0);
      const { data } = await api.post("/shipping/calculate", { origin, destination: districtId, weight: Math.max(totalWeight, 1000), courier: "jne:sicepat:jnt:tiki:pos:anteraja" });
      const opts = [];
      if (data.data && Array.isArray(data.data)) {
        for (const item of data.data) {
          if (item.cost > 0) {
            opts.push({ courier: item.name || item.code || "", service: item.service || "", description: item.description || "", cost: item.cost, etd: item.etd || "-" });
          }
        }
      }
      opts.sort((a, b) => a.cost - b.cost);
      setShippingOptions(opts);
      if (opts.length > 0) {
        setSelectedShipping(opts[0]);
        toast.success(`${opts.length} opsi pengiriman ditemukan`);
      } else {
        toast.info("Tidak ada opsi pengiriman tersedia untuk tujuan ini");
      }
    } catch (err) {
      console.error("Shipping calc error:", err);
      toast.error("Gagal menghitung ongkir");
    } finally { setCalcShipping(false); }
  };

  const updateQty = async (productId, qty) => {
    try { const { data } = await api.put(`/cart/${productId}`, { qty }); setCart(data); } catch { toast.error("Gagal mengubah jumlah"); }
  };

  const removeItem = async (productId) => {
    try { await api.delete(`/cart/${productId}`); fetchCart(); } catch { toast.error("Gagal menghapus item"); }
  };

  const items = cart?.items || [];
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const shippingCost = selectedShipping?.cost || 0;
  const grandTotal = subtotal + shippingCost;

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Keranjang kosong");
    if (!form.name.trim()) return toast.error("Nama lengkap harus diisi");
    if (!form.phone.trim()) return toast.error("Nomor WhatsApp harus diisi");
    if (!selProvince || !selCity || !selDistrict) return toast.error("Lengkapi alamat pengiriman (Provinsi, Kota, Kecamatan)");
    if (!form.address_detail.trim()) return toast.error("Detail alamat (jalan, no rumah) harus diisi");
    if (!selectedShipping) return toast.error("Pilih ekspedisi pengiriman terlebih dahulu");
    setProcessing(true);
    const fullAddress = `${form.address_detail}, ${subdistrictName ? subdistrictName + ", " : ""}${districtName}, ${cityName}, ${provinceName} ${postalCode}`;
    try {
      const { data } = await api.post("/orders", {
        name: form.name, phone: form.phone, email: form.email,
        address: fullAddress, city: cityName, postal_code: postalCode,
        payment_method: "qris_midtrans", notes: form.notes,
        shipping_cost: shippingCost,
        shipping_courier: selectedShipping ? `${selectedShipping.courier} - ${selectedShipping.service}` : "",
        destination_district_id: selDistrict,
        province_name: provinceName, city_name: cityName, district_name: districtName, subdistrict_name: subdistrictName,
      });
      setOrder(data);
      // Always use Midtrans Snap for QRIS payment
      if (midtransReady && window.snap) {
        try {
          const { data: snapData } = await api.post("/payments/snap-token", { order_id: data.id });
          window.snap.pay(snapData.token, {
            onSuccess: () => { toast.success("Pembayaran QRIS berhasil!"); navigate(`/payment/success?order_id=${data.id}`); },
            onPending: () => { toast.info("Menunggu pembayaran QRIS"); setStep("success"); },
            onError: () => { toast.error("Pembayaran gagal"); setStep("success"); },
            onClose: () => { toast.info("Pembayaran dibatalkan. Pesanan tetap tersimpan."); setStep("success"); },
          });
          setProcessing(false); return;
        } catch { toast.error("Midtrans QRIS gagal. Pesanan tetap dibuat, hubungi admin."); }
      } else {
        toast.info("Midtrans belum dikonfigurasi. Hubungi admin untuk pembayaran.");
      }
      setStep("success");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setProcessing(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen bg-[#050505]"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;

  if (step === "success" && order) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6" data-testid="order-success">
        <div className="max-w-md text-center">
          <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
          <h1 className="font-heading text-3xl font-bold uppercase mb-2">Pesanan Berhasil!</h1>
          <p className="text-gray-400 mb-4">Kode pesanan:</p>
          <p className="font-mono text-xl text-[#E60000] font-bold mb-6">{order.order_code}</p>
          <div className="bg-[#0F0F0F] border border-[#262626] p-6 text-left mb-6">
            <p className="text-white font-semibold">Total: {formatPrice(order.total)}</p>
            {order.shipping_cost > 0 && <p className="text-gray-400 text-sm mt-1">Termasuk ongkir: {formatPrice(order.shipping_cost)}</p>}
            <p className="text-gray-400 text-sm mt-1">Metode: {order.payment_method}</p>
            <p className="text-yellow-400 text-xs mt-2">Status: Menunggu Pembayaran</p>
          </div>
          <div className="flex gap-3">
            <Link to="/toko" className="flex-1"><Button className="w-full bg-[#262626] hover:bg-[#333] text-white rounded-none h-10 font-bold uppercase text-xs">Lanjut Belanja</Button></Link>
            <Link to="/portal" className="flex-1"><Button className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white rounded-none h-10 font-bold uppercase text-xs">Lihat Pesanan</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/toko" className="text-gray-400 hover:text-white" data-testid="cart-back"><ArrowLeft size={18} /></Link>
          <ShoppingCart size={20} className="text-[#E60000]" />
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">{step === "cart" ? "Keranjang Belanja" : "Checkout"}</h1>
        </div>

        {step === "cart" && (
          <>
            {items.length === 0 ? (
              <div className="text-center py-16">
                <ShoppingCart size={48} className="text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 mb-4">Keranjang Anda kosong</p>
                <Link to="/toko"><Button className="bg-[#E60000] hover:bg-[#FF1A1A] text-white rounded-none h-10 font-bold uppercase text-xs" data-testid="shop-now-btn">Belanja Sekarang</Button></Link>
              </div>
            ) : (
              <div data-testid="cart-items">
                {items.map((item, idx) => (
                  <div key={idx} className="bg-[#0F0F0F] border border-[#262626] p-4 mb-3 flex items-center justify-between" data-testid={`cart-item-${idx}`}>
                    <div className="flex-1"><p className="text-white font-semibold">{item.name}</p><p className="text-[#E60000] text-sm">{formatPrice(item.price)}</p></div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQty(item.product_id, item.qty - 1)} className="w-7 h-7 flex items-center justify-center bg-[#262626] text-white hover:bg-[#333]" data-testid={`qty-minus-${idx}`}><Minus size={12} /></button>
                      <span className="text-white w-8 text-center font-bold">{item.qty}</span>
                      <button onClick={() => updateQty(item.product_id, item.qty + 1)} className="w-7 h-7 flex items-center justify-center bg-[#262626] text-white hover:bg-[#333]" data-testid={`qty-plus-${idx}`}><Plus size={12} /></button>
                      <button onClick={() => removeItem(item.product_id)} className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-[#E60000] ml-2" data-testid={`remove-${idx}`}><Trash2 size={14} /></button>
                      <span className="text-white font-semibold ml-4 w-24 text-right">{formatPrice(item.price * item.qty)}</span>
                    </div>
                  </div>
                ))}
                <div className="bg-[#0F0F0F] border border-[#262626] p-4 mt-4 flex justify-between items-center">
                  <span className="font-heading text-lg font-bold uppercase">Subtotal</span>
                  <span className="font-heading text-2xl font-bold text-[#E60000]">{formatPrice(subtotal)}</span>
                </div>
                <Button onClick={() => setStep("checkout")} className="w-full mt-4 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider rounded-none h-12 text-base" data-testid="proceed-checkout-btn">Lanjut ke Checkout</Button>
              </div>
            )}
          </>
        )}

        {step === "checkout" && (
          <form onSubmit={handleCheckout} className="space-y-6" data-testid="checkout-form">
            {/* Contact Info */}
            <div className="bg-[#0F0F0F] border border-[#262626] p-6">
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white mb-4">Informasi Penerima</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-gray-400 text-xs uppercase">Nama Lengkap</Label><Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="checkout-name" /></div>
                <div><Label className="text-gray-400 text-xs uppercase">No. Telepon</Label><Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required data-testid="checkout-phone" /></div>
                <div className="md:col-span-2"><Label className="text-gray-400 text-xs uppercase">Email</Label><Input type="email" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="checkout-email" /></div>
              </div>
            </div>

            {/* Shipping Address - Cascading */}
            <div className="bg-[#0F0F0F] border border-[#262626] p-6">
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white mb-4">Alamat Pengiriman</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400 text-xs uppercase">Provinsi</Label>
                  <Select value={selProvince} onValueChange={onProvinceChange}>
                    <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="select-province"><SelectValue placeholder="Pilih Provinsi..." /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#262626] max-h-60">{provinces.map(p => <SelectItem key={p.id} value={String(p.id)} className="text-white">{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-400 text-xs uppercase">Kota / Kabupaten</Label>
                  <Select value={selCity} onValueChange={onCityChange} disabled={!selProvince}>
                    <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="select-city"><SelectValue placeholder={selProvince ? "Pilih Kota..." : "Pilih provinsi dulu"} /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#262626] max-h-60">{cities.map(c => <SelectItem key={c.id} value={String(c.id)} className="text-white">{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-400 text-xs uppercase">Kecamatan</Label>
                  <Select value={selDistrict} onValueChange={onDistrictChange} disabled={!selCity}>
                    <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="select-district"><SelectValue placeholder={selCity ? "Pilih Kecamatan..." : "Pilih kota dulu"} /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#262626] max-h-60">{districts.map(d => <SelectItem key={d.id} value={String(d.id)} className="text-white">{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-400 text-xs uppercase">Kelurahan</Label>
                  <Select value={selSubdistrict} onValueChange={onSubdistrictChange} disabled={!selDistrict}>
                    <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="select-subdistrict"><SelectValue placeholder={selDistrict ? "Pilih Kelurahan..." : "Pilih kecamatan dulu"} /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#262626] max-h-60">{subdistricts.map(s => <SelectItem key={s.id} value={String(s.id)} className="text-white">{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-400 text-xs uppercase">Kode Pos</Label>
                  <Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={postalCode} onChange={e => setPostalCode(e.target.value)} data-testid="checkout-postal" placeholder="Otomatis dari pilihan" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-gray-400 text-xs uppercase">Detail Alamat (Jalan, RT/RW, No. Rumah)</Label>
                  <Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.address_detail} onChange={e => setForm({...form, address_detail: e.target.value})} required data-testid="checkout-address" placeholder="Jl. Contoh No. 123, RT 01/RW 02" />
                </div>
              </div>
              {selProvince && selCity && selDistrict && (
                <div className="mt-3 text-xs text-gray-500">
                  Alamat: {form.address_detail && `${form.address_detail}, `}{subdistrictName && `${subdistrictName}, `}{districtName}, {cityName}, {provinceName} {postalCode}
                </div>
              )}
            </div>

            {/* Shipping Options - Grouped by Courier */}
            <div className="bg-[#0F0F0F] border border-[#262626] p-6">
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2"><Truck size={18} className="text-[#E60000]" /> Pilih Ekspedisi</h2>
              {calcShipping ? (
                <div className="flex items-center gap-2 text-gray-400 py-4"><Loader2 size={16} className="animate-spin" /> Menghitung ongkir...</div>
              ) : shippingOptions.length === 0 ? (
                <p className="text-gray-500 text-sm py-4">{selDistrict ? "Tidak ada opsi pengiriman tersedia." : "Pilih kecamatan untuk melihat opsi pengiriman"}</p>
              ) : (
                <ShippingGrouped options={shippingOptions} selected={selectedShipping} onSelect={setSelectedShipping} />
              )}
            </div>

            {/* Payment - QRIS Only via Midtrans */}
            <div className="bg-[#0F0F0F] border border-[#262626] p-6">
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2"><CreditCard size={18} className="text-[#E60000]" /> Pembayaran</h2>
              <div className="bg-[#050505] border border-[#262626] p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#E60000]/10 flex items-center justify-center"><CreditCard size={18} className="text-[#E60000]" /></div>
                <div>
                  <p className="text-white font-semibold text-sm">QRIS - Midtrans</p>
                  <p className="text-gray-500 text-xs">{midtransReady ? "Pembayaran otomatis via QRIS Midtrans" : "Midtrans belum dikonfigurasi. Hubungi admin."}</p>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-[#0F0F0F] border border-[#262626] p-6">
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white mb-4">Ringkasan</h2>
              {items.map((item, i) => (
                <div key={i} className="flex justify-between text-sm mb-1"><span className="text-gray-400">{item.name} x{item.qty}</span><span className="text-white">{formatPrice(item.price * item.qty)}</span></div>
              ))}
              <div className="border-t border-[#262626] mt-3 pt-3 space-y-1">
                <div className="flex justify-between text-sm"><span className="text-gray-400">Subtotal</span><span className="text-white">{formatPrice(subtotal)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-400">Ongkir{selectedShipping ? ` (${selectedShipping.courier} - ${selectedShipping.service})` : ""}</span><span className="text-white">{shippingCost > 0 ? formatPrice(shippingCost) : "-"}</span></div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-[#262626]">
                  <span className="font-heading uppercase">Total</span><span className="text-[#E60000] font-heading">{formatPrice(grandTotal)}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button type="button" onClick={() => setStep("cart")} variant="outline" className="border-[#262626] text-white rounded-none h-12 flex-1" data-testid="back-to-cart">Kembali</Button>
              <Button type="submit" disabled={processing} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider rounded-none h-12 flex-1" data-testid="place-order-btn">{processing ? "Memproses..." : "Buat Pesanan"}</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ShippingGrouped({ options, selected, onSelect }) {
  const [expanded, setExpanded] = useState({});

  // Group by courier name
  const grouped = {};
  for (const opt of options) {
    const key = opt.courier;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(opt);
  }

  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  // Auto-expand group containing selected option
  const courierNames = Object.keys(grouped);

  return (
    <div className="space-y-2 max-h-[400px] overflow-auto" data-testid="shipping-options">
      {courierNames.map(courier => {
        const services = grouped[courier];
        const lowestCost = Math.min(...services.map(s => s.cost));
        const isExpanded = expanded[courier] ?? services.some(s => s === selected);
        const hasSelected = services.some(s => s === selected);

        return (
          <div key={courier} className={`border transition-colors ${hasSelected ? "border-[#E60000]" : "border-[#262626]"}`} data-testid={`courier-group-${courier}`}>
            {/* Courier Header */}
            <button type="button" onClick={() => toggle(courier)} className="w-full flex items-center justify-between p-3 hover:bg-[#1A1A1A] transition-colors" data-testid={`toggle-${courier}`}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold ${hasSelected ? "bg-[#E60000] text-white" : "bg-[#262626] text-gray-400"}`}>
                  <Truck size={14} />
                </div>
                <div className="text-left">
                  <p className="text-white text-sm font-semibold">{courier}</p>
                  <p className="text-gray-500 text-xs">{services.length} layanan | mulai {formatPrice(lowestCost)}</p>
                </div>
              </div>
              <ChevronDown size={16} className={`text-gray-400 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
            </button>

            {/* Services List */}
            {isExpanded && (
              <div className="border-t border-[#262626]">
                {services.map((svc, i) => (
                  <button key={i} type="button" onClick={() => onSelect(svc)} className={`w-full text-left px-3 py-2.5 pl-14 flex items-center justify-between transition-colors ${selected === svc ? "bg-[#E60000]/10 border-l-2 border-l-[#E60000]" : "hover:bg-[#1A1A1A]"}`} data-testid={`shipping-opt-${courier}-${i}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 border-2 ${selected === svc ? "border-[#E60000] bg-[#E60000]" : "border-[#444]"}`} />
                      <div>
                        <p className={`text-sm font-semibold ${selected === svc ? "text-[#E60000]" : "text-white"}`}>{svc.service}</p>
                        <p className="text-gray-500 text-xs">{svc.description}{svc.etd && svc.etd !== "-" ? ` | ${svc.etd}` : ""}</p>
                      </div>
                    </div>
                    <span className={`font-heading font-bold ${selected === svc ? "text-[#E60000]" : "text-gray-300"}`}>{formatPrice(svc.cost)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}